/**
 * Mantenimiento Facturas CXP — migración web de ares8520.
 *
 * Lista las facturas de cuentas por pagar por rango de fechas (y suplidor
 * opcional) y permite:
 *   - Abrir el detalle de la factura a pantalla completa
 *     (`facturadetallecxp`), con sus chasis, sus pagos y las acciones sobre
 *     cada chasis. Antes era un diálogo de solo lectura.
 *   - Anular una factura vigente sin pagos (vplicado = 0): la anulación
 *     deja la factura en valor 0 y NO toca el inventario (fiel al
 *     escritorio) — los productos que entraron por ella siguen en el
 *     inventario y deben recibir otro tratamiento.
 *
 * A diferencia del escritorio, los permisos de borrado se validan de
 * verdad en el backend (en ares8520 estaban rotos).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  PageHeader,
  fpInputSx,
  fpDataGridSx,
  StatusPill,
  RazonCancelacionField,
  TooltipRazonCancelacion,
} from "components/financepro";
import { useApi } from "lib/api";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { imprimirFacturaCxp } from "modules/cuentasxpagar/impresionfacturacxp";
import FacturaDetalleCxp from "modules/cuentasxpagar/facturadetallecxp";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin facturas en el rango
      </Typography>
    </Stack>
  );
}

export default function MantenimientoFacturasCxp({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [suplidores, setSuplidores] = useState([]);
  const [suplidor, setSuplidor] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarSuplidores = async () => {
    const { ok, data } = await post("/api/cxp_suplidores", {});
    if (ok) setSuplidores(data.suplidores || []);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const body = { fechadesde: fecha1, fechahasta: fecha2 };
      if (suplidor) body.suplidor = suplidor.id;
      const { ok, data } = await post("/api/cxp_facturas_listar", body);
      if (ok) {
        setRows((data.facturas || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({ message: data.Error || "No se pudieron listar las facturas", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarSuplidores();
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Ver detalle ----
  // El detalle es una pantalla completa aparte (como en venta x mayor): se
  // le pasa el id y él carga lo suyo. Antes era un diálogo de solo lectura y
  // para tocar los chasis había que salir a otra pantalla.
  const [detalleId, setDetalleId] = useState(null);

  // ---- Impresión / reimpresión de la factura o conduce ----
  const [imprimiendo, setImprimiendo] = useState(0);
  const imprimir = async (row) => {
    setImprimiendo(row.id);
    try {
      const { ok, data } = await post("/api/cxp_factura_detalle", { id: row.id });
      if (ok) imprimirFacturaCxp(data);
      else showAlert({ message: data.Error || "No se pudo cargar la factura", color: "error" });
    } finally {
      setImprimiendo(0);
    }
  };

  // ---- Anulación ----
  const [target, setTarget] = useState(null);
  const [razon, setRazon] = useState("");
  // Cada factura que se abre para cancelar empieza sin razón.
  useEffect(() => setRazon(""), [target]);
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/cxp_factura_anular", { id: target.id, razon });
      if (ok) {
        showAlert({ message: data.mensajeok || "Factura anulada", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular la factura", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  const estadoChip = (row) => {
    if (row.anulada) {
      return (
        <TooltipRazonCancelacion razon={row.razonanula}>
          <Chip
            size="small"
            label="Anulada"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        </TooltipRazonCancelacion>
      );
    }
    if (row.status === "C") {
      return (
        <Chip
          size="small"
          label="Saldada"
          sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Vigente"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  };

  const columns = [
    { field: "nfactura", headerName: "Fact#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "suplidornombre", headerName: "Suplidor", flex: 1, minWidth: 180 },
    {
      field: "tipofactura",
      headerName: "Tipo",
      width: 130,
      // El valor mostrado de una consignada es solo lo vendido; el total del
      // conduce viaja en `valorconduce`.
      renderCell: (params) =>
        params.row.consignada ? <StatusPill label="Consignado" variant="info" /> : params.value,
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vplicado",
      headerName: "Pagado",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => estadoChip(params.row),
    },
    {
      field: "acciones",
      headerName: "",
      width: 310,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setDetalleId(params.row.id)}
            sx={btnOutline(FP.color.accentBlue)}
          >
            Detalle
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={!!params.row.anulada || Number(params.row.vplicado) !== 0}
            sx={btnOutline(FP.color.danger)}
          >
            Anular
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => imprimir(params.row)}
            disabled={imprimiendo === params.row.id}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Mantenimiento Facturas CXP"
          subtitle="Consulta y anula las facturas de cuentas por pagar a suplidores"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                autoHighlight
                options={suplidores}
                value={suplidor}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setSuplidor(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Suplidor (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} facturas
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Detalle a pantalla completa, con chasis y pagos */}
      <Dialog fullScreen open={!!detalleId} onClose={() => setDetalleId(null)}>
        {detalleId && (
          <FacturaDetalleCxp
            facturaId={detalleId}
            onExit={() => {
              setDetalleId(null);
              cargar();
            }}
            onChanged={cargar}
          />
        )}
      </Dialog>

      {/* Confirmación de anulación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular factura</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Factura <b>{target?.nfactura}</b> de <b>{target?.suplidornombre}</b> por RD${" "}
            <b>{fmtMoney(target?.valor)}</b>. La factura quedará anulada. Los productos que entraron
            por ella SIGUEN en el inventario y deben recibir otro tratamiento. ¿Desea continuar?
          </DialogContentText>
          <RazonCancelacionField value={razon} onChange={setRazon} disabled={anulando} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
